import { useState, useEffect, useContext } from 'react';
import { feedbackMessages } from '../constants';
import {
  getAllOrganizations,
  getAllFacilities,
  getFacilitiesByOrganization,
} from '../services/apiServices';
import { FETCH_FACILITIES_AFTER_LOGIN } from '../services/config';
import { LoadingContext, useFeedbackContext } from '../context';
import { serviceLogger } from '../utils/serviceLogger';

/**
 * Custom hook to fetch and manage organizations and facilities data.
 *
 *  This hook:
 *  - Checks if the user is an admin. If true, fetches all organizations; otherwise,
 *    fetches facilities for the user's organization.
 *  - Displays a global loading indicator during requests.
 *  - Shows error toasts via the global FeedbackContext if fetching fails.
 *
 * @param {boolean} isUserAdmin - Indicates if the user has admin privileges.
 * @param {string|number} userOrganization - The identifier of the user's organization.
 * @returns {Object} - Returns organizations and facilities data.
 */
const useGetAllOrganizationsAndFacilities = (isUserAdmin, userOrganization) => {
  const [organizations, setOrganizations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const { setIsLoading } = useContext(LoadingContext);

  const { showToast } = useFeedbackContext();

  /**
   * Fetches all organizations if the user is an admin.
   * Shows a loading state and handles errors with toast notifications.
   */
  useEffect(() => {
    if (isUserAdmin) {
      const fetchOrganizations = async () => {
        setIsLoading(true);
        try {
          const data = await getAllOrganizations();
          setOrganizations(data);
        } catch (error) {
          showToast({
            variant: feedbackMessages.ERROR_FETCH_ORGANIZATIONS.variant,
            title: feedbackMessages.ERROR_FETCH_ORGANIZATIONS.title,
            copy: feedbackMessages.ERROR_FETCH_ORGANIZATIONS.copy,
          });
          serviceLogger.error('Error fetching organizations:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAdmin]);

  /**
   * Fetches all facilities.
   * Shows a loading state and handles errors with toast notifications.
   */
  useEffect(() => {
    const fetchAllFacilities = async () => {
      setIsLoading(true);
      try {
        let data;
        if (isUserAdmin) {
          data = await getAllFacilities();
        } else if (FETCH_FACILITIES_AFTER_LOGIN && userOrganization != null && userOrganization !== '') {
          data = await getFacilitiesByOrganization(userOrganization);
        } else {
          data = [];
        }
        setFacilities(data ?? []);
      } catch (error) {
        showToast({
          variant: feedbackMessages.ERROR_FETCH_FACILITIES.variant,
          title: feedbackMessages.ERROR_FETCH_FACILITIES.title,
          copy: feedbackMessages.ERROR_FETCH_FACILITIES.copy,
        });
        serviceLogger.error('Error fetching facilities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { organizations, facilities };
};

export default useGetAllOrganizationsAndFacilities;
